import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FounderPulseTicker — A thin scrolling banner across the top of the admin dashboard
 * showing real-time KPIs from the same /api/insights/digest endpoint used by the weekly email.
 */
const FounderPulseTicker = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/insights/quick`, { withCredentials: true });
        setData(res.data?.data);
      } catch (err) { /* silent — non-admin users will get 403 */ }
    };
    fetch();
    const interval = setInterval(fetch, 5 * 60 * 1000); // refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  if (!data) return null;

  const items = [
    { icon: '📝', label: `${data.performance.review_count} reviews this week` },
    { icon: '📊', label: `Avg score: ${data.performance.avg_score}` },
    { icon: '💼', label: `${data.hiring.new_requisitions} new requisitions (${data.hiring.open_total} open)` },
    { icon: '🤝', label: `${data.meetings.completed} 1:1s · ${data.meetings.pending_action_items} action items pending` },
    { icon: '🎉', label: `${data.engagement.witties_earned} Witties earned` },
    ...(data.engagement.top_wordle_solver ? [{ icon: '🏆', label: `Top Wordle: ${data.engagement.top_wordle_solver} (${data.engagement.top_wordle_streak}-day streak)` }] : []),
    { icon: '👥', label: `${data.operations.active_employees} active employees` },
    { icon: '🌴', label: `${data.operations.pending_leaves} leaves pending` },
    { icon: '🧾', label: `${data.operations.pending_expenses} expenses pending` },
  ];

  // Duplicate items so the marquee loops seamlessly
  const ticker = [...items, ...items];

  return (
    <div
      className="relative overflow-hidden border-b border-purple-200 dark:border-purple-900/40 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 dark:from-purple-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 py-2"
      data-testid="founder-pulse-ticker"
    >
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-purple-50 dark:from-purple-900/30 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-purple-50 dark:from-purple-900/30 to-transparent z-10 pointer-events-none" />
      <div className="flex items-center">
        <span className="px-3 py-0.5 mx-2 rounded-full bg-purple-600 text-white text-[10px] font-bold uppercase tracking-wider shrink-0 animate-pulse">⚡ Founder Pulse</span>
        <div className="flex gap-8 animate-ticker whitespace-nowrap">
          {ticker.map((it, i) => (
            <span key={i} className="text-xs font-medium text-purple-900 dark:text-purple-100 flex items-center gap-1.5">
              <span>{it.icon}</span>
              <span>{it.label}</span>
              <span className="text-purple-300 dark:text-purple-700">·</span>
            </span>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-ticker { animation: ticker 60s linear infinite; }
        .animate-ticker:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
};

export default FounderPulseTicker;
